/**
 * Praesto typography — C3F v18.
 *
 * - Instrument Sans: wordmark, display, headings, UI, body. One voice for
 *   brand and interface.
 * - JetBrains Mono: eyebrows, numbers, times, timestamps, status labels.
 *   Never for body copy.
 *
 * The runtime source of truth is design/system/tokens/typography.css. The
 * scale admits EXACTLY 11/12/13/14/16/18/19/24 px plus the fluid clamps of
 * h2/h1/display: a value outside the scale is a defect.
 */
export const fonts = {
  sans: '"Instrument Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
  mono: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
} as const;

export const fontWeights = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;

/** Full type scale, mirroring design/system/tokens/typography.css. */
export const typeScale = {
  displayXl: {
    fontSize: "clamp(48px, 7vw, 96px)",
    lineHeight: "0.95",
    letterSpacing: "-0.04em",
    fontWeight: fontWeights.bold,
  },
  display: {
    fontSize: "clamp(40px, 5.4vw, 72px)",
    lineHeight: "0.95",
    letterSpacing: "-0.04em",
    fontWeight: fontWeights.bold,
  },
  h1: {
    fontSize: "clamp(32px, 4vw, 52px)",
    lineHeight: "1.02",
    letterSpacing: "-0.03em",
    fontWeight: fontWeights.bold,
  },
  h2: {
    fontSize: "clamp(26px, 3vw, 38px)",
    lineHeight: "1.08",
    letterSpacing: "-0.025em",
    fontWeight: fontWeights.bold,
  },
  h3: {
    fontSize: "24px",
    lineHeight: "1.2",
    letterSpacing: "-0.02em",
    fontWeight: fontWeights.semibold,
  },
  h4: {
    fontSize: "19px",
    lineHeight: "1.3",
    letterSpacing: "-0.015em",
    fontWeight: fontWeights.semibold,
  },
  lede: {
    fontSize: "18px",
    lineHeight: "1.5",
    fontWeight: fontWeights.regular,
  },
  body: {
    fontSize: "16px",
    lineHeight: "1.55",
    letterSpacing: "-0.005em",
    fontWeight: fontWeights.regular,
  },
  bodySm: {
    fontSize: "14px",
    lineHeight: "1.45",
    letterSpacing: "-0.005em",
    fontWeight: fontWeights.regular,
  },
  caption: {
    fontSize: "13px",
    lineHeight: "1.45",
    fontWeight: fontWeights.regular,
  },
  eyebrow: {
    fontFamily: fonts.mono,
    fontSize: "11px",
    letterSpacing: "0.1em",
    textTransform: "uppercase" as const,
    fontWeight: fontWeights.semibold,
  },
  mono: {
    fontFamily: fonts.mono,
    fontSize: "12px",
    letterSpacing: "0.03em",
    fontWeight: fontWeights.regular,
  },
  monoLg: {
    fontFamily: fonts.mono,
    fontSize: "14px",
    letterSpacing: "0.03em",
    fontWeight: fontWeights.regular,
  },
} as const;
